import { HttpClient, HttpEvent, HttpHandler, HttpHeaders, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { HttpRequestHeadersEnum } from '@enums/http-request-headers.enum';
import { UrlFilenameEnum } from '@enums/url-filename.enum';
import { UrlPathnameEnum } from '@enums/url-pathname.enum';
import { UrlEnum } from '@enums/url.enum';
import { AnalyticsService } from '@shared/services/analytics/analytics.service';
import { ConfigService } from '@shared/services/config/config.service';
import { LocalStorageService } from '@shared/services/local-storage/local-storage.service';
import { from, Observable } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { TokenExpireConfigService } from '../token-expire-config/token-expire-config.service';

export interface TokenPostResponse {
  $id: string;
  authenticationSuccess: boolean;
  token: string;
  tokenExpiry: string;
  refreshToken?: string;
}

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  constructor(
    @Inject(AnalyticsService) private _analyticsService: AnalyticsService,
    @Inject(LocalStorageService) private _localStorageService: LocalStorageService,
    @Inject(HttpClient) private _httpClient: HttpClient,
    @Inject(ConfigService) private _configService: ConfigService,
    @Inject(TokenExpireConfigService) private tokenExpireConfigService: TokenExpireConfigService
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const bearerToken = this._localStorageService.getBearerToken();
    const sessionId = this._localStorageService.getUserSessionToken();
    const hasSessionId = sessionId && this.shouldIncludeSessionId(request.url);

    if (!this.omitRequestUrl(request.url)) {
      request = request.clone({
        setHeaders: {
          [HttpRequestHeadersEnum.ContentType]: HttpRequestHeadersEnum.ApplicationJson,
          Authorization: bearerToken,
        },
        setParams: { t: new Date().getTime().toString() },
      });
    }
    if (bearerToken) {
      request = request.clone({
        setHeaders: {
          Authorization: bearerToken,
          ...(hasSessionId ? { SessionId: sessionId } : {}),
        },
      });
    }
    return from(this.verifyToken(request, next));
  }

  public omitRequestUrl(requestUrl: string): boolean {
    if (
      requestUrl.endsWith(UrlPathnameEnum.Authorize) ||
      requestUrl.endsWith(UrlPathnameEnum.Refresh) ||
      requestUrl.endsWith(UrlPathnameEnum.Token) ||
      requestUrl.endsWith(UrlFilenameEnum.ico) ||
      requestUrl.endsWith(UrlFilenameEnum.json)
    ) {
      return true;
    }
    return false;
  }

  public sendRefreshTokenRequest(request?: HttpRequest<any>, next?: HttpHandler, refreshToken?: string): Observable<HttpEvent<any>> {
    const authApi = this._configService.config && this._configService.getProperty<string>(UrlEnum.AUTH_API_URL);
    const refreshTokenUrl = `${authApi}/${UrlPathnameEnum.Token}/${UrlPathnameEnum.Refresh}`;
    const headers = new HttpHeaders().set(HttpRequestHeadersEnum.ContentType, HttpRequestHeadersEnum.ApplicationJson);

    const data = JSON.stringify({
      Token: this._localStorageService.getBearerTokenValue(),
      RefreshToken: refreshToken,
    });

    return this._httpClient.post(refreshTokenUrl, data, { headers }).pipe(
      switchMap((response: any) => {
        this.storeTokenInfo(response);
        return next.handle(request);
      })
    );
  }

  public verifyToken(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const token: string = this._localStorageService.getBearerTokenValue(false);
    const storedTokenExpiry: string = this._localStorageService.getTokenExpiry();
    const isTokenExpired: boolean = token ? this.tokenExpireConfigService.isTokenExpired(storedTokenExpiry) : true;
    const refreshToken: string = this._localStorageService.getRefreshToken();

    if (token && isTokenExpired && refreshToken && !this.omitRequestUrl(request.url)) {
      return this.sendRefreshTokenRequest(request, next, refreshToken);
    } else {
      return next.handle(request).pipe(
        tap((event: HttpEvent<any>) => {
          if (request.url.endsWith(UrlPathnameEnum.Token) && event instanceof HttpResponse) {
            this.storeTokenInfo(event.body);
          }
        })
      );
    }
  }

  private storeTokenInfo(response: TokenPostResponse): void {
    this._localStorageService.setBearerToken(response);
    this._localStorageService.setTokenExpiry(response.tokenExpiry);
    this._localStorageService.setRefreshToken(response.refreshToken);
  }

  shouldIncludeSessionId(url: string): boolean {
    const orderApi = this._configService.config && this._configService.getProperty<string>(UrlEnum.ORDER_API_URL);
    return orderApi && url.includes(orderApi);
  }
}
